import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import { downloadDatasheetStatementPdf, useIssueStatement, type IssueStatementBody } from "@/lib/datasheets";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  datasheetId: number;
}

export function IssueStatementDialog({ open, onOpenChange, datasheetId }: Props) {
  const issue = useIssueStatement(datasheetId);
  const [method, setMethod] = useState<"Pdf" | "Email">("Pdf");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [message, setMessage] = useState("");
  const [from, setFrom] = useState("");
  const [toDate, setToDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canIssue = (method === "Pdf" || to.trim().length > 0) && !submitting;

  async function submit() {
    setSubmitting(true);
    try {
      const body: IssueStatementBody = {
        deliveryMethod: method,
        emailTo: method === "Email" ? to.trim() : null,
        emailCc: method === "Email" && cc ? cc : null,
        emailBcc: method === "Email" && bcc ? bcc : null,
        message: method === "Email" && message ? message : null,
        from: from || null,
        to: toDate || null,
      };
      const result = await issue.mutateAsync(body);
      toast.success(result.emailed ? `Emailed to ${result.emailTo}` : "Statement PDF generated");
      // For a PDF issue, hand the operator the document they just generated.
      if (!result.emailed) void downloadDatasheetStatementPdf(datasheetId, from || undefined, toDate || undefined);
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof ApiError && e.status === 400 ? "Invalid — check the fields." : "Could not issue the statement");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Issue statement</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-3">
            <div className="space-y-1">
              <Label htmlFor="s-from">From date</Label>
              <Input id="s-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="s-to-date">To date</Label>
              <Input id="s-to-date" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="s-method">Delivery method</Label>
            <select
              id="s-method"
              aria-label="Delivery method"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={method}
              onChange={(e) => setMethod(e.target.value as "Pdf" | "Email")}
            >
              <option value="Pdf">PDF</option>
              <option value="Email">Email</option>
            </select>
          </div>
          {method === "Email" ? (
            <>
              <div className="space-y-1">
                <Label htmlFor="s-to-addr">To</Label>
                <Input id="s-to-addr" value={to} onChange={(e) => setTo(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="s-cc">Cc</Label>
                <Input id="s-cc" value={cc} onChange={(e) => setCc(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="s-bcc">Bcc</Label>
                <Input id="s-bcc" value={bcc} onChange={(e) => setBcc(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="s-msg">Message</Label>
                <Input id="s-msg" value={message} onChange={(e) => setMessage(e.target.value)} />
              </div>
            </>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!canIssue}>
            Issue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
